import React, { useState } from 'react'
import { assetService } from '@templatean/shared/services'
import { Asset } from '@templatean/shared/types'
import { LoadingSpinner } from '@templatean/shared/components'
import { PowerPointService } from '@templatean/shared/services/office.service'
import { useOffice } from '@templatean/shared/hooks'

interface ColorScheme {
  name: string
  primary: string
  secondary: string
  accent: string
  background: string
  text: string
}

const DesignAssistant: React.FC = () => {
  const { service } = useOffice()
  const [activeSection, setActiveSection] = useState<'themes' | 'colors' | 'fonts' | 'assets'>('themes')
  const [selectedTheme, setSelectedTheme] = useState<string>('modern')
  const [brandAssets, setBrandAssets] = useState<Asset[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const powerPointService = service as PowerPointService

  const themes = [
    { id: 'modern', name: 'Modern', description: 'Clean and contemporary' },
    { id: 'corporate', name: 'Corporate', description: 'Professional business style' },
    { id: 'creative', name: 'Creative', description: 'Bold and artistic' },
    { id: 'minimal', name: 'Minimal', description: 'Simple and elegant' },
    { id: 'tech', name: 'Tech', description: 'Technology-focused design' },
    { id: 'vibrant', name: 'Vibrant', description: 'Colorful and energetic' },
  ]

  const colorSchemes: ColorScheme[] = [
    {
      name: 'Ocean Blue',
      primary: '#0066CC',
      secondary: '#004499',
      accent: '#00AAFF',
      background: '#F0F8FF',
      text: '#333333'
    },
    {
      name: 'Forest Green',
      primary: '#228B22',
      secondary: '#006400',
      accent: '#32CD32',
      background: '#F0FFF0',
      text: '#2F4F2F'
    },
    {
      name: 'Sunset Orange',
      primary: '#FF6347',
      secondary: '#FF4500',
      accent: '#FFA500',
      background: '#FFF5EE',
      text: '#8B4513'
    },
    {
      name: 'Royal Purple',
      primary: '#6A0DAD',
      secondary: '#4B0082',
      accent: '#9370DB',
      background: '#F8F0FF',
      text: '#2F004F'
    },
    {
      name: 'Monochrome',
      primary: '#000000',
      secondary: '#333333',
      accent: '#666666',
      background: '#FFFFFF',
      text: '#000000'
    },
  ]

  const fontPairs = [
    { heading: 'Helvetica', body: 'Arial', style: 'Clean & Modern' },
    { heading: 'Georgia', body: 'Times New Roman', style: 'Classic & Elegant' },
    { heading: 'Futura', body: 'Helvetica', style: 'Bold & Contemporary' },
    { heading: 'Playfair Display', body: 'Source Sans Pro', style: 'Sophisticated' },
    { heading: 'Montserrat', body: 'Open Sans', style: 'Friendly & Approachable' },
  ]

  const handleApplyTheme = async (themeId: string) => {
    try {
      setSelectedTheme(themeId)
      await powerPointService.applyTheme(themeId)
      alert(`Applied ${themeId} theme successfully!`)
    } catch (error) {
      console.error('Failed to apply theme:', error)
      setError('Failed to apply theme')
    }
  }

  const handleApplyColorScheme = async (scheme: ColorScheme) => {
    try {
      // Apply color scheme to presentation
      // Note: PowerPoint API limitations - this would need custom implementation
      console.log('Applying color scheme:', scheme)
      alert(`Applied ${scheme.name} color scheme!`)
    } catch (error) {
      console.error('Failed to apply color scheme:', error)
      setError('Failed to apply color scheme')
    }
  }

  const loadBrandAssets = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await assetService.getAssets({
        tags: ['brand', 'logo', 'powerpoint'],
        fileTypes: ['image/png', 'image/jpeg', 'image/svg+xml'],
        pageSize: 20
      })
      setBrandAssets(response.items)
    } catch (err) {
      setError('Failed to load brand assets')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleInsertAsset = async (asset: Asset) => {
    try {
      // Fetch asset as base64
      const response = await fetch(asset.url)
      const blob = await response.blob()
      const base64 = await blobToBase64(blob)
      
      await powerPointService.insertImage(base64.split(',')[1], {
        width: 200,
        height: 150
      })
      
      alert(`Inserted ${asset.name} successfully!`)
    } catch (error) {
      console.error('Failed to insert asset:', error)
      setError('Failed to insert asset')
    }
  }

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  React.useEffect(() => {
    if (activeSection === 'assets') {
      loadBrandAssets()
    }
  }, [activeSection])

  return (
    <div className="design-assistant">
      <h3>Design Assistant</h3>
      
      <div className="design-tabs">
        <button
          className={`tab ${activeSection === 'themes' ? 'active' : ''}`}
          onClick={() => setActiveSection('themes')}
        >
          Themes
        </button>
        <button
          className={`tab ${activeSection === 'colors' ? 'active' : ''}`}
          onClick={() => setActiveSection('colors')}
        >
          Colors
        </button>
        <button
          className={`tab ${activeSection === 'fonts' ? 'active' : ''}`}
          onClick={() => setActiveSection('fonts')}
        >
          Fonts
        </button>
        <button
          className={`tab ${activeSection === 'assets' ? 'active' : ''}`}
          onClick={() => setActiveSection('assets')}
        >
          Brand Assets
        </button>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {activeSection === 'themes' && (
        <div className="themes-section">
          <p className="description">Choose a presentation theme</p>
          <div className="theme-grid">
            {themes.map((theme) => (
              <div
                key={theme.id}
                className={`theme-card ${selectedTheme === theme.id ? 'selected' : ''}`}
                onClick={() => handleApplyTheme(theme.id)}
              >
                <div className="theme-preview">
                  <div className={`theme-visual theme-${theme.id}`}></div>
                </div>
                <h4>{theme.name}</h4>
                <p>{theme.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSection === 'colors' && (
        <div className="colors-section">
          <p className="description">Select a color scheme for your presentation</p>
          <div className="color-schemes">
            {colorSchemes.map((scheme) => (
              <div
                key={scheme.name}
                className="color-scheme"
                onClick={() => handleApplyColorScheme(scheme)}
              >
                <div className="color-preview">
                  <div 
                    className="color-swatch primary" 
                    style={{ backgroundColor: scheme.primary }}
                  ></div>
                  <div 
                    className="color-swatch secondary" 
                    style={{ backgroundColor: scheme.secondary }}
                  ></div>
                  <div 
                    className="color-swatch accent" 
                    style={{ backgroundColor: scheme.accent }}
                  ></div>
                  <div 
                    className="color-swatch background" 
                    style={{ backgroundColor: scheme.background }}
                  ></div>
                </div>
                <div className="color-info">
                  <h4>{scheme.name}</h4>
                  <div className="color-codes">
                    <span>Primary: {scheme.primary}</span>
                    <span>Accent: {scheme.accent}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSection === 'fonts' && (
        <div className="fonts-section">
          <p className="description">Choose a font combination</p>
          <div className="font-pairs">
            {fontPairs.map((pair, index) => (
              <div
                key={index}
                className="font-pair"
                onClick={() => alert(`Applied ${pair.heading} + ${pair.body} fonts`)}
              >
                <div className="font-preview">
                  <h4 style={{ fontFamily: pair.heading }}>
                    {pair.heading}
                  </h4>
                  <p style={{ fontFamily: pair.body }}>
                    {pair.body} - Lorem ipsum dolor sit amet
                  </p>
                </div>
                <span className="font-style">{pair.style}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSection === 'assets' && (
        <div className="assets-section">
          <p className="description">Insert brand assets into your presentation</p>
          
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="asset-grid">
              {brandAssets.map((asset) => (
                <div key={asset.id} className="asset-item">
                  {asset.thumbnailUrl && (
                    <img 
                      src={asset.thumbnailUrl} 
                      alt={asset.name}
                      className="asset-thumbnail"
                    />
                  )}
                  <div className="asset-info">
                    <h5>{asset.name}</h5>
                    <button
                      onClick={() => handleInsertAsset(asset)}
                      className="btn btn-primary btn-sm"
                    >
                      Insert
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {brandAssets.length === 0 && !loading && (
            <div className="empty-state">
              <p>No brand assets found</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DesignAssistant